import React from 'react';
import { Content } from '@/types/content';
import { cn } from '@/lib/utils';
import { useList } from '@/contexts/ListContext';
import { Check, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AspectRatio } from '@/components/ui/aspect-ratio';
interface ContentCardProps {
  content: Content;
  className?: string;
  onClick?: () => void;
}
const ContentCard: React.FC<ContentCardProps> = ({
  content,
  className,
  onClick
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const location = useLocation();
  const isVideoLibrary = location.pathname === '/videos';
  const {
    addToList,
    removeFromList,
    isInList
  } = useList();
  const contentId = content.id || '';
  const inMyList = isInList(contentId);
  const thumbnailUrl = content.thumbnailUrl || content.thumbnail || '';
  const handleCardClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/videos/${content.slug || content.id}`);
    }
  };
  const handlePlayVideo = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/videos/${content.slug || content.id}`);
  };
  const handleToggleList = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (inMyList) {
      removeFromList(content.id);
    } else {
      addToList(content);
    }
  };
  if (isMobile) {
    if (isVideoLibrary) {
      return <div className={cn('flex-shrink-0 w-[220px] cursor-pointer snap-start', className)} onClick={handleCardClick}>
          <div className="relative overflow-hidden">
            <AspectRatio ratio={4 / 5}>
              <img src={thumbnailUrl} alt={content.title} loading="lazy" className="w-full h-full object-cover" />
            </AspectRatio>
            <div className="absolute inset-0 flex items-center justify-center">
              <img src="/lovable-uploads/575a2bb1-d107-4904-aee1-b9abecc713b6.png" alt="Play" className="w-8 h-8 md:w-10 md:h-10" />
            </div>
          </div>
          <div className="mt-2 px-0">
            <h3 className="text-sm font-medium line-clamp-2 py-[6px]">{content.title}</h3>
          </div>
        </div>;
    }
    return <div className={cn('flex-shrink-0 w-[242px] cursor-pointer snap-start', className)} onClick={handleCardClick}>
        <div className="relative overflow-hidden">
          <AspectRatio ratio={16 / 9}>
            <img src={thumbnailUrl} alt={content.title} loading="lazy" className="w-full h-full object-cover" />
          </AspectRatio>
          <div className="absolute inset-0 flex items-center justify-center">
            <img src="/lovable-uploads/575a2bb1-d107-4904-aee1-b9abecc713b6.png" alt="Play" className="w-6 h-6 md:w-8 md:h-8" />
          </div>
        </div>
        <div className="mt-2 px-0">
          <h3 className="text-sm font-normal line-clamp-2">{content.title}</h3>
          <p className="text-xs text-gray-500 mt-1">{content.genre}</p>
        </div>
      </div>;
  }
  return <ContextMenu>
      <ContextMenuTrigger asChild>
        <div className={cn('relative cursor-pointer w-full', className)} onClick={handleCardClick}>
          <div className="relative overflow-hidden">
            <AspectRatio ratio={16 / 9}>
              <img src={thumbnailUrl} alt={content.title} loading="lazy" className="w-full h-full object-cover" />
            </AspectRatio>
            
            <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <Button variant="play" size="playSmall" onClick={handlePlayVideo} aria-label="Play">
                <img src="/lovable-uploads/575a2bb1-d107-4904-aee1-b9abecc713b6.png" alt="Play" className="w-8 h-8 md:w-10 md:h-10" />
              </Button>
            </div>
            
            <div className="absolute inset-0 flex items-center justify-center">
              <img src="/lovable-uploads/575a2bb1-d107-4904-aee1-b9abecc713b6.png" alt="Play" className="w-6 h-6 md:w-8 md:h-8" />
            </div>
          </div>
          {isVideoLibrary && <div className="mt-2">
              <h3 className="text-sm font-medium line-clamp-2">{content.title}</h3>
            </div>}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        <ContextMenuItem onClick={handlePlayVideo}>
          Play
        </ContextMenuItem>
        <ContextMenuItem onClick={handleToggleList}>
          {inMyList ? "Remove from My List" : "Add to My List"}
        </ContextMenuItem>
        <ContextMenuItem onClick={handleCardClick}>
          More Info
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>;
};
export default ContentCard;